import React, { useState, useEffect } from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
import LazyLoad from 'react-lazyload';
// template components
import ContentHub from '@nmx/template/dist/componentLibrary/modules/ContentHub';
import InPageLeadForm from '@nmx/template/dist/componentLibrary/modules/InPageLeadForm';
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
// local components
import AdvisorConnectModule from './components/advisorConnectModule';
import CredentialCalloutGrouping from './components/credentialCalloutGrouping';
import Faq from './components/faq';
import FinancialPlanningSection from './components/financialPlanningSection';
import HeroSection from './components/heroSection';
import InPageLeadFormLazyLoadContainer from '../../components/inPageLeadFormLazyLoadContainer';
import IntroSection from './components/introSection';
import ProductCallout from './components/productCallout';
import ProductComparisonChart from './components/productComparisonChart';
import QuizCallout from './components/quizCallout';
import UnderstandingProduct from './components/understandingProduct';
// local metadata
import Metadata from './components/metadata';
// native image assets
import highResDesktopImage from './components/heroSection/images/annuities-hero-desktop.webp';
import highResMobileImage from './components/heroSection/images/annuities-hero-mobile.webp';
// dislaimer footnotes
import {
  AdditionalFundOptions,
  HighestFinancialStrengthRatings,
  LoyaltyBasedOnClientData,
} from '../../../utils/disclaimerFootnotes/disclaimerFootnotes';
// other utils
import { getImmediateMatchFeatureFlag } from '../../../utils/getFeatureFlags/getFeatureFlags';

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps, contentHubItems } = useRouteData();
  // We don't want to load recaptcha on page load (for performance reasons) so we will load it once the in page lead for is engaged with
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);
  const [immediateMatchFeatureFlag, setImmediateMatchFeatureFlag] = useState(false);

  useEffect(() => {
    getImmediateMatchFeatureFlag(setImmediateMatchFeatureFlag, config);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      config={config}
      dynamicConfig={dynamicConfig}
      useReCaptcha={loadRecaptcha}
      disableDisclaimerTopBorder
      breadcrumbsNavigationConfig={[
        {
          pageName: 'Home',
          linkUrl: '/',
          linkAriaLabel: 'Northwestern Mutual Home',
        },
        {
          pageName: 'Insurance',
          linkUrl: '/insurance/',
          linkAriaLabel: 'Northwestern Mutual Insurance',
        },
        {
          pageName: 'Income Annuities',
          linkUrl: '/annuities/',
          linkAriaLabel: 'Northwestern Mutual Income Annuities',
        },
      ]}
      disclaimerFootnotes={[
        <>
          {LoyaltyBasedOnClientData(1)}
        </>,
        <>
          {HighestFinancialStrengthRatings(2)}
        </>,
        <>
          Income annuities have no cash value. Once issued, the annuity cannot be terminated (surrendered), and the premium paid for the annuity is not refundable and cannot be withdrawn.
        </>,
        <>
          Distributions taken from the contract may be subject to ordinary income tax. A 10&#37; federal tax penalty may apply if you take any type of distribution from the contract before age 59&#189;.
        </>,
        <>
          Northwestern Mutual is the marketing name for The Northwestern Mutual Life Insurance Company (NM), Milwaukee, WI (life and disability insurance, annuities, and life insurance with long&#45;term care benefits) and its subsidiaries. Northwestern Long Term Care Insurance Company, Milwaukee, WI (long&#45;term care insurance) is a subsidiary of NM.
        </>,
        <>
          To be used with form numbers: ICC17.UU.PSPIA.(0717), 90-2737-91(1021), ICC17.UU.PDIA.(0717), ICC17.PDIA.CDD.AMT.(0717),
          ICC17.PSPIA.CDD.AMT.(0717), ICC11.NN.SPIA.(0511), ICC16.NN.DIA.(1116), ICC12.RS.SPRA.(0313), ICC12.RS.SPRA.5.(0313), ICC12.RS.SPRA.LD.(0313)
        </>,
      ]}
      customDisclaimers={[
        <>
          Mason Street Advisors, LLC (MSA) is the principal investment adviser for all the Portfolios in the Northwestern Mutual Series Fund (Series Fund). (Portfolios in the Series Fund are identified by the parenthetical next to the portfolio name.) MSA has engaged and oversees sub&#45;advisers who provide day&#45;to&#45;day management for certain of the Series Fund Portfolios. Each sub&#45;adviser may be replaced without the approval of shareholders. Please see the prospectus or summary prospectus for more information.{' '}{AdditionalFundOptions()}
        </>,
        <>
          Past performance is not a guarantee of future results.
        </>,
        <>
          For more complete information please refer to the prospectus or summary prospectus which you can obtain from your Northwestern Mutual Investment Services Registered Representative. Read the prospectus and summary prospectus carefully and consider the investment objectives, risks, and charges and expenses carefully before you invest or send money.
        </>,
        <>
          Issuer: The Northwestern Mutual Life Insurance Company, 720 E. Wisconsin Avenue, Milwaukee, WI 53202&#45;4797
        </>,
        <>
          Principal Underwriter: Northwestern Mutual Investment Services, LLC (NMIS) (investment brokerage services), subsidiary of NM, registered investment adviser, broker&#45;dealer, member FINRA and SIPC.
        </>,
        <>
          Variable Joint Life
        </>,
        <>
          Variable Executive Life
        </>,
        <>
          Custom Variable Universal Life
        </>,
        <>
          Executive Variable Universal Life
        </>,
        <>
          Survivorship Variable Universal Life
        </>,
        <>
          Variable Universal Life Plus
        </>,
      ]}
      stateFilingComplianceNumber={<>90&#8211;2737&#8211;91 (0224)</>}>

      <HeroSection />

      <Head>
        {/* prefetch expensive images */}
        <link rel='preload' href={highResMobileImage} as='image' media={`(max-width: ${Theme.breakpoints.values.medium}px)`} />
        <link rel='preload' href={highResDesktopImage} as='image' media={`(min-width: ${Theme.breakpoints.values.medium + 1}px)`} />
      </Head>

      <Metadata
        image={highResDesktopImage}
        pageDescription={pageProps.pageDescription}
        pageTitle={pageProps.pageTitle}
      />

      <IntroSection />

      <UnderstandingProduct />

      <LazyLoad
        offset={200}
        height={300}
        once
        placeholder={
          <section id="quiz-callout-income-annuities">
            <h2>How does an annuity work&#63; Test your knowledge.</h2>
          </section>
        }
      >
        <QuizCallout />
      </LazyLoad>

      <Faq />

      <ProductCallout />

      <InPageLeadFormLazyLoadContainer>
        <InPageLeadForm
          sectionId='in-page-lead' // note: id in-page-lead-form will be on the Form element itself.
          config={config}
          formLocation='annuities'
          immediateMatchFeatureFlagEnabled={immediateMatchFeatureFlag}
          onFormEngaged={() => {
            if (!loadRecaptcha) {
              setLoadRecaptcha(true);
            }
          }}
          variant='embedded'
        />
      </InPageLeadFormLazyLoadContainer>

      <ProductComparisonChart />

      <FinancialPlanningSection />

      <CredentialCalloutGrouping />

      <ContentHub
        sectionId="content-hub"
        headingText="Boost your annuities knowledge"
        contentHubItems={contentHubItems} // defined in route.js
        config={config}
      />

      <AdvisorConnectModule />

    </Page>
  );
};
